import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Save, Plus, Trash2, Edit2, X, ChevronDown, ChevronUp, GripVertical, Image, Star } from 'lucide-react';
import './AdminCMS.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCMS = () => {
  const [activeTab, setActiveTab] = useState('institucional');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Data states
  const [pageContent, setPageContent] = useState([]);
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  
  // Edit modal states
  const [editModal, setEditModal] = useState({ open: false, type: '', data: null });

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [contentRes, servicesRes, projectsRes] = await Promise.all([
        axios.get(`${API}/cms/content/${activeTab}`),
        axios.get(`${API}/cms/services/all`),
        axios.get(`${API}/cms/projects/all`)
      ]);
      
      setPageContent(contentRes.data);
      setServices(servicesRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error loading CMS data:', error);
      showMessage('error', 'Erro ao carregar dados');
    }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveContent = async (page, section, content) => {
    setSaving(true);
    try {
      await axios.put(`${API}/cms/content/${page}/${section}`, { content });
      showMessage('success', 'Conteúdo guardado com sucesso!');
      loadData();
    } catch (error) {
      showMessage('error', 'Erro ao guardar conteúdo');
    }
    setSaving(false);
  };

  const handleSaveService = async (slug, data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/cms/services/${slug}`, data);
      showMessage('success', 'Serviço guardado com sucesso!');
      setEditModal({ open: false, type: '', data: null });
      loadData();
    } catch (error) {
      showMessage('error', 'Erro ao guardar serviço');
    }
    setSaving(false);
  };

  const handleSaveProject = async (slug, data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/cms/projects/${slug}`, data);
      showMessage('success', 'Projeto guardado com sucesso!');
      setEditModal({ open: false, type: '', data: null });
      loadData();
    } catch (error) {
      showMessage('error', 'Erro ao guardar projeto');
    }
    setSaving(false);
  };

  const handleCreateService = async (data) => {
    setSaving(true);
    try {
      await axios.post(`${API}/cms/services`, data);
      showMessage('success', 'Serviço criado com sucesso!');
      setEditModal({ open: false, type: '', data: null });
      loadData();
    } catch (error) {
      showMessage('error', 'Erro ao criar serviço');
    }
    setSaving(false);
  };

  const handleCreateProject = async (data) => {
    setSaving(true);
    try {
      await axios.post(`${API}/cms/projects`, data);
      showMessage('success', 'Projeto criado com sucesso!');
      setEditModal({ open: false, type: '', data: null });
      loadData();
    } catch (error) {
      showMessage('error', 'Erro ao criar projeto');
    }
    setSaving(false);
  };

  const handleDeleteService = async (slug) => {
    if (!window.confirm('Tem certeza que deseja eliminar este serviço?')) return;
    try {
      await axios.delete(`${API}/cms/services/${slug}`);
      showMessage('success', 'Serviço eliminado');
      loadData();
    } catch (error) {
      showMessage('error', 'Erro ao eliminar serviço');
    }
  };

  const handleDeleteProject = async (slug) => {
    if (!window.confirm('Tem certeza que deseja eliminar este projeto?')) return;
    try {
      await axios.delete(`${API}/cms/projects/${slug}`);
      showMessage('success', 'Projeto eliminado');
      loadData();
    } catch (error) {
      showMessage('error', 'Erro ao eliminar projeto');
    }
  };

  const handleToggleProjectFeatured = async (project) => {
    try {
      await axios.put(`${API}/cms/projects/${project.slug}`, {
        is_featured: !project.is_featured
      });
      showMessage('success', project.is_featured ? 'Destaque removido' : 'Projeto destacado na homepage');
      loadData();
    } catch (error) {
      showMessage('error', 'Erro ao atualizar destaque');
    }
  };

  // Convert Google Drive link to direct view link (thumbnail format is more reliable)
  const convertGoogleDriveLink = (url) => {
    if (!url) return url;
    const patterns = [
      /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,
      /https:\/\/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/,
      /https:\/\/drive\.google\.com\/uc\?.*id=([a-zA-Z0-9_-]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1000`;
      }
    }
    return url;
  };

  const tabs = [
    { id: 'institucional', label: 'Institucional' },
    { id: 'intervencao', label: 'Intervenção' },
    { id: 'projetos', label: 'Projetos' },
    { id: 'homepage', label: 'Homepage' }
  ];

  return (
    <div className="admin-cms" data-testid="admin-cms">
      <div className="cms-header">
        <h1>Gestão de Conteúdos</h1>
        <p>Edite os conteúdos das páginas do site</p>
      </div>

      {message.text && (
        <div className={`cms-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="cms-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`cms-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="cms-loading">A carregar...</div>
      ) : (
        <div className="cms-content">
          {activeTab === 'institucional' && (
            <InstitucionalTab 
              content={pageContent} 
              onSave={handleSaveContent}
              saving={saving}
            />
          )}
          
          {activeTab === 'intervencao' && (
            <IntervencaoTab 
              content={pageContent}
              services={services}
              onSaveContent={handleSaveContent}
              onEditService={(service) => setEditModal({ open: true, type: 'service', data: service })}
              onDeleteService={handleDeleteService}
              onAddService={() => setEditModal({ open: true, type: 'service', data: null })}
              saving={saving}
            />
          )}
          
          {activeTab === 'projetos' && (
            <ProjetosTab 
              content={pageContent}
              projects={projects}
              onSaveContent={handleSaveContent}
              onEditProject={(project) => setEditModal({ open: true, type: 'project', data: project })}
              onDeleteProject={handleDeleteProject}
              onAddProject={() => setEditModal({ open: true, type: 'project', data: null })}
              onToggleFeatured={handleToggleProjectFeatured}
              saving={saving}
            />
          )}
          
          {activeTab === 'homepage' && (
            <HomepageTab 
              content={pageContent} 
              onSave={handleSaveContent}
              saving={saving}
            />
          )}
        </div>
      )}

      {/* Edit Modal */}
      {editModal.open && (
        <EditModal 
          type={editModal.type}
          data={editModal.data}
          onClose={() => setEditModal({ open: false, type: '', data: null })}
          onSaveService={editModal.data ? handleSaveService : handleCreateService}
          onSaveProject={editModal.data ? handleSaveProject : handleCreateProject}
          convertGoogleDriveLink={convertGoogleDriveLink}
          saving={saving}
        />
      )}
    </div>
  );
};


// ============ INSTITUCIONAL TAB ============
const InstitucionalTab = ({ content, onSave, saving }) => {
  const [expandedSections, setExpandedSections] = useState(['missao']);
  const [editedContent, setEditedContent] = useState({});

  useEffect(() => {
    const initial = {};
    content.forEach(item => {
      initial[item.section] = item.content;
    });
    setEditedContent(initial);
  }, [content]);

  const toggleSection = (section) => {
    setExpandedSections(prev => 
      prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
    );
  };

  const handleContentChange = (section, field, value) => {
    setEditedContent(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const handleNestedChange = (section, index, field, value) => {
    setEditedContent(prev => {
      const items = [...(prev[section]?.items || [])];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, [section]: { ...prev[section], items } };
    });
  };

  const handleOrgaoChange = (section, orgaoIndex, membroIndex, field, value) => {
    setEditedContent(prev => {
      const orgaos = [...(prev[section]?.orgaos || [])];
      if (membroIndex !== null) {
        orgaos[orgaoIndex].membros[membroIndex][field] = value;
      } else {
        orgaos[orgaoIndex][field] = value;
      }
      return { ...prev, [section]: { ...prev[section], orgaos } };
    });
  };

  const handleMembroChange = (section, index, field, value) => {
    setEditedContent(prev => {
      const membros = [...(prev[section]?.membros || [])];
      membros[index] = { ...membros[index], [field]: value };
      return { ...prev, [section]: { ...prev[section], membros } };
    });
  };

  const addMembro = (section) => {
    setEditedContent(prev => {
      const membros = [...(prev[section]?.membros || []), { nome: '', cargo: '', foto: '' }];
      return { ...prev, [section]: { ...prev[section], membros } };
    });
  };

  const removeMembro = (section, index) => {
    setEditedContent(prev => {
      const membros = (prev[section]?.membros || []).filter((_, i) => i !== index);
      return { ...prev, [section]: { ...prev[section], membros } };
    });
  };

  const sections = [
    { id: 'hero', title: 'Hero / Cabeçalho' },
    { id: 'missao', title: 'Missão' },
    { id: 'visao', title: 'Visão' },
    { id: 'valores', title: 'Valores' },
    { id: 'orgaos_sociais', title: 'Órgãos Sociais' },
    { id: 'equipa', title: 'Equipa' },
    { id: 'sustentabilidade', title: 'Sustentabilidade' },
    { id: 'compromissos', title: 'Compromissos' }
  ];

  return (
    <div className="tab-content">
      {sections.map(section => {
        const sectionContent = editedContent[section.id] || {};
        const isExpanded = expandedSections.includes(section.id);
        
        return (
          <div key={section.id} className="cms-section">
            <div className="section-header" onClick={() => toggleSection(section.id)}>
              <h3>{section.title}</h3>
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
            
            {isExpanded && (
              <div className="section-body">
                {section.id === 'hero' && (
                  <>
                    <div className="form-group">
                      <label>Título</label>
                      <input 
                        type="text" 
                        value={sectionContent.title || ''} 
                        onChange={(e) => handleContentChange(section.id, 'title', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Descrição</label>
                      <textarea 
                        value={sectionContent.description || ''} 
                        onChange={(e) => handleContentChange(section.id, 'description', e.target.value)}
                        rows={3}
                      />
                    </div>
                  </>
                )}

                {(section.id === 'missao' || section.id === 'visao' || section.id === 'sustentabilidade') && (
                  <>
                    <div className="form-group">
                      <label>Título</label>
                      <input 
                        type="text" 
                        value={sectionContent.title || ''} 
                        onChange={(e) => handleContentChange(section.id, 'title', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Parágrafo 1</label>
                      <textarea 
                        value={sectionContent.text1 || ''} 
                        onChange={(e) => handleContentChange(section.id, 'text1', e.target.value)}
                        rows={3}
                      />
                    </div>
                    <div className="form-group">
                      <label>Parágrafo 2</label>
                      <textarea 
                        value={sectionContent.text2 || ''} 
                        onChange={(e) => handleContentChange(section.id, 'text2', e.target.value)}
                        rows={3}
                      />
                    </div>
                    <div className="form-group">
                      <label>URL da Imagem</label>
                      <input 
                        type="text" 
                        value={sectionContent.image || ''} 
                        onChange={(e) => handleContentChange(section.id, 'image', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                  </>
                )}

                {section.id === 'valores' && (
                  <>
                    <div className="form-group">
                      <label>Título da Secção</label>
                      <input 
                        type="text" 
                        value={sectionContent.title || ''} 
                        onChange={(e) => handleContentChange(section.id, 'title', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Subtítulo</label>
                      <input 
                        type="text" 
                        value={sectionContent.subtitle || ''} 
                        onChange={(e) => handleContentChange(section.id, 'subtitle', e.target.value)}
                      />
                    </div>
                    <h4>Valores (Cards)</h4>
                    {(sectionContent.items || []).map((item, idx) => (
                      <div key={idx} className="nested-item">
                        <div className="form-group">
                          <label>Título do Valor {idx + 1}</label>
                          <input 
                            type="text" 
                            value={item.title || ''} 
                            onChange={(e) => handleNestedChange(section.id, idx, 'title', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label>Descrição</label>
                          <textarea 
                            value={item.description || ''} 
                            onChange={(e) => handleNestedChange(section.id, idx, 'description', e.target.value)}
                            rows={2}
                          />
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {section.id === 'orgaos_sociais' && (
                  <>
                    <div className="form-group">
                      <label>Título</label>
                      <input 
                        type="text" 
                        value={sectionContent.title || ''} 
                        onChange={(e) => handleContentChange(section.id, 'title', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Quadriénio</label>
                      <input 
                        type="text" 
                        value={sectionContent.quadrienio || ''} 
                        onChange={(e) => handleContentChange(section.id, 'quadrienio', e.target.value)}
                        placeholder="Quadriénio 2022-2025"
                      />
                    </div>
                    <div className="form-group">
                      <label>Descrição</label>
                      <textarea 
                        value={sectionContent.description || ''} 
                        onChange={(e) => handleContentChange(section.id, 'description', e.target.value)}
                        rows={2}
                      />
                    </div>
                    
                    {(sectionContent.orgaos || []).map((orgao, orgaoIdx) => (
                      <div key={orgaoIdx} className="orgao-section">
                        <h4>{orgao.nome}</h4>
                        {(orgao.membros || []).map((membro, membroIdx) => (
                          <div key={membroIdx} className="membro-row">
                            <div className="form-group half">
                              <label>Cargo</label>
                              <input 
                                type="text" 
                                value={membro.cargo || ''} 
                                onChange={(e) => handleOrgaoChange(section.id, orgaoIdx, membroIdx, 'cargo', e.target.value)}
                              />
                            </div>
                            <div className="form-group half">
                              <label>Nome</label>
                              <input 
                                type="text" 
                                value={membro.nome || ''} 
                                onChange={(e) => handleOrgaoChange(section.id, orgaoIdx, membroIdx, 'nome', e.target.value)}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </>
                )}

                {section.id === 'equipa' && (
                  <>
                    <div className="form-group">
                      <label>Título</label>
                      <input 
                        type="text" 
                        value={sectionContent.title || ''} 
                        onChange={(e) => handleContentChange(section.id, 'title', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Descrição</label>
                      <textarea 
                        value={sectionContent.description || ''} 
                        onChange={(e) => handleContentChange(section.id, 'description', e.target.value)}
                        rows={2}
                      />
                    </div>
                    <h4>Membros da Equipa</h4>
                    {(sectionContent.membros || []).map((membro, idx) => (
                      <div key={idx} className="nested-item">
                        <div className="nested-header">
                          <span>Membro {idx + 1}</span>
                          <button type="button" onClick={() => removeMembro(section.id, idx)} className="remove-btn">
                            <span>✕</span>
                          </button>
                        </div>
                        <div className="form-group">
                          <label>Nome</label>
                          <input 
                            type="text" 
                            value={membro.nome || ''} 
                            onChange={(e) => handleMembroChange(section.id, idx, 'nome', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label>Cargo</label>
                          <input 
                            type="text" 
                            value={membro.cargo || ''} 
                            onChange={(e) => handleMembroChange(section.id, idx, 'cargo', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label>Foto (URL ou link Google Drive)</label>
                          <input 
                            type="text" 
                            value={membro.foto || ''} 
                            onChange={(e) => handleMembroChange(section.id, idx, 'foto', e.target.value)}
                            placeholder="https://drive.google.com/file/d/..."
                          />
                        </div>
                      </div>
                    ))}
                    <button type="button" className="add-item-btn" onClick={() => addMembro(section.id)}>
                      + Adicionar Membro
                    </button>
                  </>
                )}

                {section.id === 'compromissos' && (
                  <>
                    <div className="form-group">
                      <label>Título</label>
                      <input 
                        type="text" 
                        value={sectionContent.title || ''} 
                        onChange={(e) => handleContentChange(section.id, 'title', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Subtítulo</label>
                      <input 
                        type="text" 
                        value={sectionContent.subtitle || ''} 
                        onChange={(e) => handleContentChange(section.id, 'subtitle', e.target.value)}
                      />
                    </div>
                    <h4>Lista de Compromissos</h4>
                    {(sectionContent.items || []).map((item, idx) => (
                      <div key={idx} className="form-group">
                        <label>Compromisso {idx + 1}</label>
                        <textarea 
                          value={item || ''} 
                          onChange={(e) => {
                            const items = [...(sectionContent.items || [])];
                            items[idx] = e.target.value;
                            handleContentChange(section.id, 'items', items);
                          }}
                          rows={2}
                        />
                      </div>
                    ))}
                  </>
                )}

                <button 
                  className="save-section-btn"
                  onClick={() => onSave('institucional', section.id, sectionContent)}
                  disabled={saving}
                >
                  <Save size={16} />
                  {saving ? 'A guardar...' : 'Guardar Secção'}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};


// ============ INTERVENÇÃO TAB ============
const IntervencaoTab = ({ content, services, onSaveContent, onEditService, onDeleteService, onAddService, saving }) => {
  const [heroContent, setHeroContent] = useState({});

  useEffect(() => {
    const hero = content.find(c => c.section === 'hero');
    if (hero) setHeroContent(hero.content || {});
  }, [content]);

  return (
    <div className="tab-content">
      {/* Hero Section */}
      <div className="cms-section">
        <div className="section-header">
          <h3>Cabeçalho da Página</h3>
        </div>
        <div className="section-body">
          <div className="form-group">
            <label>Título</label>
            <input 
              type="text" 
              value={heroContent.title || ''} 
              onChange={(e) => setHeroContent(prev => ({ ...prev, title: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label>Descrição</label>
            <textarea 
              value={heroContent.description || ''} 
              onChange={(e) => setHeroContent(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
            />
          </div>
          <button 
            className="save-section-btn"
            onClick={() => onSaveContent('intervencao', 'hero', heroContent)}
            disabled={saving}
          >
            <Save size={16} />
            {saving ? 'A guardar...' : 'Guardar'}
          </button>
        </div>
      </div>

      {/* Services List */}
      <div className="cms-section">
        <div className="section-header">
          <h3>Serviços / Áreas de Intervenção</h3>
          <button className="add-btn" onClick={onAddService}>
            <Plus size={16} /> Adicionar Serviço
          </button>
        </div>
        <div className="section-body">
          <div className="items-list">
            {services.map((service, idx) => (
              <div key={service.slug} className={`list-item ${!service.is_active ? 'inactive' : ''}`}>
                <div className="item-order">{idx + 1}</div>
                <div className="item-info">
                  <h4>{service.title}</h4>
                  <p>{service.hero_description?.substring(0, 100)}...</p>
                  {!service.is_active && <span className="inactive-badge">Inativo</span>}
                </div>
                <div className="item-actions">
                  <button className="edit-btn" onClick={() => onEditService(service)}>
                    <Edit2 size={16} />
                  </button>
                  <button className="delete-btn" onClick={() => onDeleteService(service.slug)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};


// ============ PROJETOS TAB ============
const ProjetosTab = ({ content, projects, onSaveContent, onEditProject, onDeleteProject, onAddProject, onToggleFeatured, saving }) => {
  const [heroContent, setHeroContent] = useState({});

  useEffect(() => {
    const hero = content.find(c => c.section === 'hero');
    if (hero) setHeroContent(hero.content || {});
  }, [content]);

  return (
    <div className="tab-content">
      {/* Hero Section */}
      <div className="cms-section">
        <div className="section-header">
          <h3>Cabeçalho da Página</h3>
        </div>
        <div className="section-body">
          <div className="form-group">
            <label>Título</label>
            <input 
              type="text" 
              value={heroContent.title || ''} 
              onChange={(e) => setHeroContent(prev => ({ ...prev, title: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label>Descrição</label>
            <textarea 
              value={heroContent.description || ''} 
              onChange={(e) => setHeroContent(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
            />
          </div>
          <button 
            className="save-section-btn"
            onClick={() => onSaveContent('projetos', 'hero', heroContent)}
            disabled={saving}
          >
            <Save size={16} />
            {saving ? 'A guardar...' : 'Guardar'}
          </button>
        </div>
      </div>

      {/* Projects List */}
      <div className="cms-section">
        <div className="section-header">
          <h3>Lista de Projetos</h3>
          <button className="add-btn" onClick={onAddProject}>
            <Plus size={16} /> Adicionar Projeto
          </button>
        </div>
        <div className="section-body">
          <div className="items-list">
            {projects.map((project, idx) => (
              <div key={project.slug} className={`list-item ${!project.is_active ? 'inactive' : ''}`}>
                <div className="item-order">{idx + 1}</div>
                <div className="item-info">
                  <h4>
                    {project.title}
                    {project.is_featured && (
                      <Star size={14} fill="#f59e0b" color="#f59e0b" style={{marginLeft: 8, verticalAlign: 'middle'}} />
                    )}
                  </h4>
                  <p>{project.hero_description?.substring(0, 100)}...</p>
                  {!project.is_active && <span className="inactive-badge">Inativo</span>}
                  {project.is_featured && <span className="inactive-badge" style={{background:'#fef3c7',color:'#92400e'}}>Destacado na Homepage</span>}
                </div>
                <div className="item-actions">
                  <button
                    className="edit-btn"
                    onClick={() => onToggleFeatured(project)}
                    title={project.is_featured ? 'Remover destaque da homepage' : 'Destacar na homepage'}
                    data-testid={`toggle-featured-${project.slug}`}
                    style={{background: project.is_featured ? '#fef3c7' : undefined}}
                  >
                    <Star size={16} fill={project.is_featured ? '#f59e0b' : 'none'} color={project.is_featured ? '#f59e0b' : undefined} />
                  </button>
                  <button className="edit-btn" onClick={() => onEditProject(project)}>
                    <Edit2 size={16} />
                  </button>
                  <button className="delete-btn" onClick={() => onDeleteProject(project.slug)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};


// ============ HOMEPAGE TAB ============
const HomepageTab = ({ content, onSave, saving }) => {
  const [heroContent, setHeroContent] = useState({});

  useEffect(() => {
    const hero = content.find(c => c.section === 'hero');
    if (hero) setHeroContent(hero.content || {});
  }, [content]);

  const handleTitleLineChange = (index, value) => {
    const lines = [...(heroContent.title_lines || ['', '', ''])];
    lines[index] = value;
    setHeroContent(prev => ({ ...prev, title_lines: lines }));
  };

  const handleImageChange = (index, value) => {
    const images = [...(heroContent.images || ['', '', ''])];
    images[index] = value;
    setHeroContent(prev => ({ ...prev, images }));
  };

  return (
    <div className="tab-content">
      <div className="cms-section">
        <div className="section-header">
          <h3>Hero Principal</h3>
        </div>
        <div className="section-body">
          <h4>Título (3 linhas)</h4>
          {[0, 1, 2].map(i => (
            <div key={i} className="form-group">
              <label>Linha {i + 1}</label>
              <input 
                type="text" 
                value={(heroContent.title_lines || [])[i] || ''} 
                onChange={(e) => handleTitleLineChange(i, e.target.value)}
              />
            </div>
          ))}
          
          <h4>Imagens do Slider</h4>
          {[0, 1, 2].map(i => (
            <div key={i} className="form-group">
              <label>Imagem {i + 1} (URL)</label>
              <input 
                type="text" 
                value={(heroContent.images || [])[i] || ''} 
                onChange={(e) => handleImageChange(i, e.target.value)}
                placeholder="https://..."
              />
            </div>
          ))}

          <button 
            className="save-section-btn"
            onClick={() => onSave('homepage', 'hero', heroContent)}
            disabled={saving}
          >
            <Save size={16} />
            {saving ? 'A guardar...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};


// ============ EDIT MODAL ============
const EditModal = ({ type, data, onClose, onSaveService, onSaveProject, convertGoogleDriveLink, saving }) => {
  const isNew = !data;
  const [formData, setFormData] = useState(
    data || (type === 'service' 
      ? { slug: '', title: '', hero_description: '', image: '', sections: [{ title: '', content: '' }], services: [''], order: 0, is_active: true }
      : { slug: '', title: '', hero_description: '', image: '', full_content: '', objectives: [''], order: 0, is_active: true }
    )
  );

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSectionChange = (index, field, value) => {
    const sections = [...(formData.sections || [])];
    sections[index] = { ...sections[index], [field]: value };
    setFormData(prev => ({ ...prev, sections }));
  };

  const handleListItemChange = (field, index, value) => {
    const items = [...(formData[field] || [])];
    items[index] = value;
    setFormData(prev => ({ ...prev, [field]: items }));
  };

  const addSection = () => {
    setFormData(prev => ({ ...prev, sections: [...(prev.sections || []), { title: '', content: '' }] }));
  };

  const removeSection = (index) => {
    setFormData(prev => ({ ...prev, sections: prev.sections.filter((_, i) => i !== index) }));
  };

  const addListItem = (field) => {
    setFormData(prev => ({ ...prev, [field]: [...(prev[field] || []), ''] }));
  };

  const removeListItem = (field, index) => {
    setFormData(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  };

  const handleSubmit = () => {
    // Convert image URL if it's a Google Drive link
    const submitData = { ...formData };
    if (submitData.image) {
      submitData.image = convertGoogleDriveLink(submitData.image);
    }
    
    if (type === 'service') {
      if (isNew) {
        onSaveService(submitData);
      } else {
        onSaveService(data.slug, submitData);
      }
    } else {
      if (isNew) {
        onSaveProject(submitData);
      } else {
        onSaveProject(data.slug, submitData);
      }
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large">
        <div className="modal-header">
          <h2>{isNew ? 'Novo' : 'Editar'} {type === 'service' ? 'Serviço' : 'Projeto'}</h2>
          <button className="close-btn" onClick={onClose}><X size={24} /></button>
        </div>
        
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group half">
              <label>Slug (URL)*</label>
              <input 
                type="text" 
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                placeholder="ex: meu-projeto"
                disabled={!isNew}
              />
            </div>
            <div className="form-group half">
              <label>Ordem</label>
              <input 
                type="number" 
                value={formData.order}
                onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Título*</label>
            <input 
              type="text" 
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Descrição Curta*</label>
            <textarea 
              value={formData.hero_description}
              onChange={(e) => handleChange('hero_description', e.target.value)}
              rows={2}
            />
          </div>

          <div className="form-group">
            <label>URL da Imagem</label>
            <input 
              type="text" 
              value={formData.image}
              onChange={(e) => handleChange('image', e.target.value)}
              placeholder="https://... ou link do Google Drive"
            />
            <small>Links do Google Drive serão convertidos automaticamente</small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input 
                type="checkbox" 
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
              />
              Ativo (visível no site)
            </label>
          </div>

          {type === 'service' && (
            <>
              <h4>Secções de Conteúdo</h4>
              {(formData.sections || []).map((section, idx) => (
                <div key={idx} className="nested-item">
                  <div className="nested-header">
                    <span>Secção {idx + 1}</span>
                    {formData.sections.length > 1 && (
                      <button type="button" onClick={() => removeSection(idx)} className="remove-btn">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Título</label>
                    <input 
                      type="text" 
                      value={section.title || ''}
                      onChange={(e) => handleSectionChange(idx, 'title', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Conteúdo</label>
                    <textarea 
                      value={section.content || ''}
                      onChange={(e) => handleSectionChange(idx, 'content', e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
              ))}
              <button type="button" className="add-item-btn" onClick={addSection}>
                <Plus size={14} /> Adicionar Secção
              </button>

              <h4>Lista de Serviços</h4>
              {(formData.services || []).map((service, idx) => (
                <div key={idx} className="list-input-row">
                  <input 
                    type="text" 
                    value={service}
                    onChange={(e) => handleListItemChange('services', idx, e.target.value)}
                  />
                  {formData.services.length > 1 && (
                    <button type="button" onClick={() => removeListItem('services', idx)} className="remove-btn">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="add-item-btn" onClick={() => addListItem('services')}>
                <Plus size={14} /> Adicionar Item
              </button>
            </>
          )}

          {type === 'project' && (
            <>
              <div className="form-group">
                <label>Conteúdo Completo</label>
                <textarea 
                  value={formData.full_content}
                  onChange={(e) => handleChange('full_content', e.target.value)}
                  rows={6}
                  placeholder="Use duas quebras de linha para separar parágrafos"
                />
              </div>

              <h4>Objetivos</h4>
              {(formData.objectives || []).map((obj, idx) => (
                <div key={idx} className="list-input-row">
                  <input 
                    type="text" 
                    value={obj}
                    onChange={(e) => handleListItemChange('objectives', idx, e.target.value)}
                  />
                  {formData.objectives.length > 1 && (
                    <button type="button" onClick={() => removeListItem('objectives', idx)} className="remove-btn">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="add-item-btn" onClick={() => addListItem('objectives')}>
                <Plus size={14} /> Adicionar Objetivo
              </button>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>Cancelar</button>
          <button className="save-btn" onClick={handleSubmit} disabled={saving}>
            <Save size={16} />
            {saving ? 'A guardar...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminCMS;
